import { SectionEyebrow, SectionTitle, FadeUp } from "./_shared";
import ProgramCard from "./_partials/ProgramCard";
import { ProgramComparison } from "./ProgramComparison";

export const PROGRAMS = [
  {
    slug: "health-optimization",
    title: "Health Optimization",
    blurb: "Whole-person baseline — labs, biomarkers, lifestyle, recovery.",
    image:
      "https://customer-assets.emergentagent.com/job_integrated-matrix/artifacts/zpfgjyc9_g5axOtwNoO0w0JrHLqX5O.png",
    comparisonImage:
      "https://customer-assets-39nsmqrw.emergentagent.net/job_integrated-matrix/artifacts/d5r7kph3_abstract_human_figure_with_six_glowing_modality_nodes_connec.png",
    pitch:
      "A complete baseline of who you are today — biomarkers, vitals, sleep, recovery, stress and lifestyle — synthesized into a single Digital Twin so we can see exactly where to intervene first.",
    includes: [
      "Comprehensive lab + biomarker panel",
      "Wearable & sleep data integration",
      "Personalized intervention sequence",
      "Quarterly re-baseline + adjustment",
    ],
  },
  {
    slug: "body-transformation",
    title: "Body Transformation",
    blurb: "Muscle, fat, mobility — engineered to last.",
    image:
      "https://customer-assets-39nsmqrw.emergentagent.net/job_integrated-matrix/artifacts/p3gnmb7k_An_illustrated_figure_performing_progressive_muscle_relaxati.png",
    comparisonImage:
      "https://customer-assets-39nsmqrw.emergentagent.net/job_integrated-matrix/artifacts/ybzhssuw_cinematic_photorealistic_image_of_a_human_body_with_glowing_.png",
    pitch:
      "Body composition, hormones and recovery — aligned for sustainable change.",
    includes: [
      "Body composition tracking with our patent-pending technology",
      "Strength + mobility programming",
      "Macro + supplement protocol",
      "Recovery & deload windows",
    ],
  },
  {
    slug: "cognitive-optimization",
    title: "Cognitive Optimization",
    blurb: "Sharper focus, deeper recall, calmer mind.",
    image:
      "https://customer-assets-39nsmqrw.emergentagent.net/job_integrated-matrix/artifacts/9hayeu73_athlete_in_motion_with_holographic_mirror_neuron_network_ove.png",
    comparisonImage:
      "https://customer-assets-39nsmqrw.emergentagent.net/job_integrated-matrix/artifacts/j89ocpxh_bilateral_sound_waves_and_alternating_light_pulses_around_a_.png",
    pitch:
      "Sleep, inflammation, blood flow and stress — mapped into a clearer cognitive plan.",
    includes: [
      "Cognitive baseline + EEG patterns",
      "Sleep & circadian engineering",
      "Targeted nootropic stack (where appropriate)",
      "Stress & nervous-system rehab",
    ],
  },
  {
    slug: "hormone-optimization",
    title: "Hormone Optimization",
    blurb: "Restore balance — energy, libido, mood, recovery.",
    image:
      "https://customer-assets.emergentagent.com/job_integrated-matrix/artifacts/0eblgzd7_UsVtct-CH9SujJOnPzAro.png",
    comparisonImage:
      "https://customer-assets-39nsmqrw.emergentagent.net/job_integrated-matrix/artifacts/j8labzcl_An_illustrated_male_silhouette_integrated_with_DNA_helixes_a_14.png",
    pitch:
      "Hormones drive energy, libido, mood, body composition and resilience. We map full sex-hormone, thyroid, cortisol and metabolic axes — then balance, never blast.",
    includes: [
      "Comprehensive hormone panel",
      "Thyroid + cortisol mapping",
      "Lifestyle, nutrition, peptide options",
      "Quarterly titration with your physician",
    ],
  },
  {
    slug: "metabolic-optimization",
    title: "Metabolic Optimization",
    blurb: "Insulin, mitochondria, longevity at the cellular level.",
    image:
      "https://customer-assets.emergentagent.com/job_integrated-matrix/artifacts/6qcsedz2_kcBX1Um41i36YwhunKMiZ.png",
    comparisonImage:
      "https://customer-assets.emergentagent.com/job_integrated-matrix/artifacts/6qcsedz2_kcBX1Um41i36YwhunKMiZ.png",
    pitch:
      "Metabolic health is the master lever for longevity. We track glucose, insulin, mitochondrial markers and oxidative stress — and tune nutrition + movement around them.",
    includes: [
      "CGM + fasting insulin profile",
      "Mitochondrial & oxidative panel",
      "Nutrition timing + glycemic plan",
      "Exercise zone targets",
    ],
  },
  {
    slug: "performance-optimization",
    title: "Performance Optimization",
    blurb: "Push the ceiling — strength, endurance, resilience.",
    image: "https://customer-assets-39nsmqrw.emergentagent.net/job_integrated-matrix/artifacts/3vfrogy4_futuristic_athlete_standing_confident_looking_at_holographic.webp",
    comparisonImage: "https://customer-assets-39nsmqrw.emergentagent.net/job_integrated-matrix/artifacts/ww55vcz0_athlete_futuristic_glowing_almost_photorealistic__futuristic.png",
    pitch:
      "VO2 max, load, sleep and recovery — engineered for durable output.",
    includes: [
      "Metabolic testing",
      "Periodized strength + conditioning",
      "Recovery, sleep, HRV protocol",
      "Game-day fueling strategy",
    ],
  },
  {
    slug: "longevity-optimization",
    title: "Longevity Optimization",
    blurb: "Add healthy years — biological age, not just calendar age.",
    image:
      "https://customer-assets.emergentagent.com/job_integrated-matrix/artifacts/pgmjc5tu_oPyy7cjBi6FngmHB.png",
    comparisonImage:
      "https://customer-assets-39nsmqrw.emergentagent.net/job_integrated-matrix/artifacts/vfron9x6_An_illustrated_male_silhouette_integrated_with_DNA_helixes_a.png",
    pitch:
      "Longevity is engineered, not wished. We measure biological age, senescence markers and key longevity biomarkers — and run protocols that bend the curve over years.",
    includes: [
      "Biological age (epigenetic) testing",
      "Inflammation, glycation, senescence panel",
      "Nutrition, exercise & sleep targets",
      "Annual longevity review",
    ],
  },
];

export default function Programs() {
  return (
    <section
      id="programs"
      data-testid="programs-section"
      className="py-24 md:py-36 bg-card/40 border-y border-border"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <FadeUp className="text-center max-w-3xl mx-auto">
          <SectionEyebrow>Choose Your Goal</SectionEyebrow>
          <SectionTitle className="mt-5">
            Precision protocols for <em className="not-italic text-primary">your</em> blueprint.
          </SectionTitle>
          <p className="mt-6 text-lg md:text-xl text-foreground/80 leading-relaxed">
            Measured biology. Clear priorities. Physician-guided execution.
          </p>
        </FadeUp>

        <FadeUp delay={0.1} className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" as="div">
          {PROGRAMS.map((p, i) => (
            <ProgramCard key={p.slug} program={p} index={i} />
          ))}
        </FadeUp>
        <ProgramComparison programs={PROGRAMS} />
      </div>
    </section>
  );
}
